import React, {useCallback, useMemo} from 'react';
import {LayoutChangeEvent, View} from 'react-native';
import Animated, {useAnimatedStyle, useSharedValue} from 'react-native-reanimated';
import {BottomTabHeaderProps} from '@react-navigation/bottom-tabs';
import {useFocusEffect} from '@react-navigation/native';

import BrandHeader, {BrandHeaderProps} from '../../components/BrandHeader';
import {isSiteHeaderActive} from '../../components/SiteHeader/headerDenyList';
import {HeaderCompactContext} from '../../context/HeaderCompactContext';
import {useScrollCollapseContext} from '../../context/ScrollCollapseContext';

/**
 * DIS-1127 FIX: React Navigation passes fresh `navigation` and `route` object
 * references to the header slot on many internal state updates (focus transitions,
 * descriptor regeneration, etc.) — even when the user is only scrolling within a
 * single screen. Each fresh prop identity caused the HOF body to build a new
 * `<BrandHeader/>` React element, which gave `AnimatedHeader` a new `children`
 * prop identity, which forced React to reconcile the animated view mid-scroll,
 * which in turn caused Reanimated to briefly apply the static style portion
 * before re-applying the worklet-driven transform — producing visible flicker
 * on Header + StoreNameCard + Banner on Android (and web).
 *
 * Fix strategy: the HOF must return a PLAIN FUNCTION — React Navigation v7's
 * bottom-tabs invokes `options.header(props)` directly as a function (not via
 * React.createElement), so returning a React.memo component breaks it with
 * "header is not a function". Instead we render a stable memoized inner
 * component whose type identity is captured in the HOF closure (created once
 * per route config), and React's reconciler honors the memo comparator on
 * subsequent renders. This blocks the `children`-identity cascade without
 * violating React Navigation's header contract.
 *
 * BrandHeader stays mounted inside the memoized element — its MobX `observer`
 * wrapper continues to react to store mutations (cart badge, store name, etc.).
 *
 * DIS-1127 phase 2: `scrollCollapse` opts a route into the collapse-animated
 * header. Without it, the header renders as a static `<View>` with no worklet
 * and no read of the shared ScrollCollapseContext — making non-opt-in screens
 * structurally immune to cross-screen scrollY leakage. `scrollCollapse` must
 * be paired with `headerTransparent: true` at the route spec level; pairing
 * the two is enforced by convention at the call site.
 */
export const header = (
  headerConfig: BrandHeaderProps['config'],
  {scrollCollapse = false}: {scrollCollapse?: boolean} = {},
) => {
  // Stable memoized inner component — created ONCE per header(config) call.
  // Its type reference is captured in the closure of the function returned below,
  // so React's reconciler sees the same type across React Navigation's repeated
  // invocations and can honor the memo comparator.
  const MemoInner = React.memo(
    function BrandHeaderShell({navigation, route}: BottomTabHeaderProps) {
      const inner = <BrandHeader navigation={navigation} route={route} config={headerConfig} />;
      return scrollCollapse ? (
        <AnimatedHeader routeKey={route.key}>{inner}</AnimatedHeader>
      ) : (
        <StaticHeader>{inner}</StaticHeader>
      );
    },
    (prev, next) => {
      // Skip re-render when React Navigation only updated internal state references.
      // Re-render when the logical route or its params actually changed.
      return prev.route.key === next.route.key && prev.route.params === next.route.params;
    },
  );

  // Return a plain function — this is what React Navigation invokes as
  // `options.header(props)`. It constructs a React element of MemoInner type
  // (stable across calls); React reconciles against previous element of the
  // same type and runs the memo comparator instead of re-rendering.
  return (props: BottomTabHeaderProps) => <MemoInner {...props} />;
};

/**
 * Static header for routes that are NOT scroll-collapse screens.
 * Renders a plain View — no worklet, no shared-context reads, no transforms.
 * This is the correct behavior whenever the route does not set
 * `headerTransparent: true`, because without a transparent header slot the
 * collapse animation has no visual effect (the slot remains) and only exposes
 * the screen to leaked shared-context scrollY from other scroll-collapse
 * screens (the DIS-1127 phase-1 bug on PromotionDetail, ExclusiveOfferDetail,
 * etc. where the header rendered in a collapsed state on first mount).
 */
function StaticHeader({children}: {children: React.ReactNode}) {
  return (
    <View style={{zIndex: 100}} pointerEvents="box-none">
      {children}
    </View>
  );
}

function AnimatedHeader({children, routeKey}: {children: React.ReactNode; routeKey: string}) {
  const {scrollY, headerHeight} = useScrollCollapseContext();

  // Local header height for THIS screen's collapse animation.
  // Each tab screen has its own AnimatedHeader but they all share the context's
  // headerHeight. When tabs have different header heights (e.g. homepage=167px,
  // shop=84px), the last writer wins — causing incorrect collapse on tab switch
  // because onLayout doesn't re-fire when a frozen screen unfreezes (dimensions
  // unchanged). Using a local value isolates each header's collapse.
  const localHeaderHeight = useSharedValue(84);

  // `ready` gates the animated style worklet so it returns a safe
  // translateY:0 during the brief window on focus where shared values may
  // still be mid-propagation. See the useFocusEffect below for the unlock.
  const ready = useSharedValue(0);
  useFocusEffect(
    useCallback(() => {
      // DIS-1127 (regression fix, 2026-04-22):
      // Header USED to write scrollY/prevScrollY/accumulated* on focus, on the
      // assumption that its useFocusEffect listener registered before the
      // screen body's useScrollFocusSync and therefore its writes would be
      // overwritten by the body's restore. Instrumentation showed the
      // opposite order in practice on RN 0.84 + React 19 + RN Screens v4 +
      // Fabric — the body's listener runs FIRST, so the header's writes
      // clobber the body's restored lastScrollY. Combined with the known
      // async JS-mirror behaviour of SharedValue.set on this stack
      // (see db421747f), this leaves ctx.scrollY at 0 on re-focus while the
      // native FlatList still sits at its previous offset (e.g. 1320). The
      // first user scroll event then writes the real offset, producing the
      // "header expanded on return, disappears on first scroll" bug.
      //
      // Design already put scroll-state ownership in the body hooks — see
      // AnimatedTabBar.tsx:24-26 ("scrollY/prevScrollY restore is handled by
      // useFocusEffect in the scroll handler hooks"). We simply stop the
      // header from racing the body.
      //
      // Header now only: arms the ready gate, syncs its local layout height
      // into the shared context so StickyFilterBar can read it, and unlocks
      // the gate after a microtask.
      ready.set(0);
      headerHeight.set(localHeaderHeight.get());
      queueMicrotask(() => {
        ready.set(1);
      });
    }, [headerHeight, localHeaderHeight, ready]),
  );

  const onLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const height = event.nativeEvent.layout.height;
      if (height > 0) {
        localHeaderHeight.set(height);
        // Also update shared value for scroll-collapse consumers (e.g.
        // RangeProductListScreen's pill bar overlay).
        headerHeight.set(height);
      }
    },
    [localHeaderHeight, headerHeight],
  );

  // DIS-1127: Header animation is transform-only (translateY). We do NOT animate
  // marginBottom or any layout property, because doing so changes the Header's
  // layout slot during scroll, which shifts the Screen's flex area, which shifts
  // the native ScrollView's maxScrollY, which causes external scrollY clamping
  // and an infinite feedback loop on Android (visible as header flicker at the
  // collapse trigger point).
  //
  // The proper fix uses React Navigation's `headerTransparent: true` option
  // (set per-route in liquorStoreAppRoutes.tsx and flow stack navigators) to
  // take the header out of the flex layout entirely, combined with per-screen
  // paddingTop: useHeaderHeight() compensation on scroll content containers.
  // See docs/GOTCHAS.md for the full pattern.
  //
  // DIS-1840 (spec § 7.2): with SiteHeader active on web the header box no longer moves;
  // SiteHeader compacts inside its full-height box. Native and HEADER-denied web keep the
  // formula below unchanged.
  const siteHeaderActive = isSiteHeaderActive();
  const headerStyle = useAnimatedStyle(() => {
    'worklet';
    if (siteHeaderActive) return {transform: [{translateY: 0}]};
    const r = ready.get();
    const sy = scrollY.get();
    const lh = localHeaderHeight.get();
    const offset = r ? Math.min(Math.max(0, sy), lh) : 0;
    return {transform: [{translateY: -offset}]};
  });

  const scrollSignal = useMemo(() => ({scrollY, ready}), [scrollY, ready]);

  return (
    <Animated.View
      key={routeKey}
      style={[{zIndex: 100}, headerStyle]}
      onLayout={onLayout}
      pointerEvents="box-none">
      <HeaderCompactContext.Provider value={scrollSignal}>{children}</HeaderCompactContext.Provider>
    </Animated.View>
  );
}
